import api from '../lib/api'

// FAQ store API (backend `app/routers/faq.py`). Every call is scoped to one tenant.
const base = (tenantId) => `/admin/tenants/${tenantId}/faq`

export const getFaqCapabilities = (tenantId) =>
  api.get(`${base(tenantId)}/capabilities`).then((r) => r.data)

export const getFaqStatus = (tenantId) =>
  api.get(`${base(tenantId)}/status`).then((r) => r.data)

export const retryFaqSync = (tenantId) =>
  api.post(`${base(tenantId)}/sync/retry`).then((r) => r.data)

export const listFaqGroups = (tenantId) =>
  api.get(`${base(tenantId)}/groups`).then((r) => r.data.items)

// Empty values are not sent.
const sentParams = (params) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined),
)

// params: { q, group_id, source, sync_status, sort, page, page_size }.
export const listFaqEntries = (tenantId, params = {}) =>
  api.get(base(tenantId), { params: sentParams(params) }).then((r) => r.data)

export const getFaqEntry = (tenantId, entryId) =>
  api.get(`${base(tenantId)}/${entryId}`).then((r) => r.data)

// content: { question, answer, alt_phrasings, attachments, group_id }
export const createFaqEntry = (tenantId, content) =>
  api.post(base(tenantId), content).then((r) => r.data)

// changes: only the fields to change, plus `expected_updated_at` to detect a concurrent edit.
export const updateFaqEntry = (tenantId, entryId, changes) =>
  api.patch(`${base(tenantId)}/${entryId}`, changes).then((r) => r.data)

export const deleteFaqEntry = (tenantId, entryId) =>
  api.delete(`${base(tenantId)}/${entryId}`).then((r) => r.data)

export const createFaqGroup = (tenantId, name) =>
  api.post(`${base(tenantId)}/groups`, { name }).then((r) => r.data)

export const renameFaqGroup = (tenantId, groupId, name) =>
  api.patch(`${base(tenantId)}/groups/${groupId}`, { name }).then((r) => r.data)

// Returns { deleted, moved }: the group's entries become ungrouped.
export const deleteFaqGroup = (tenantId, groupId) =>
  api.delete(`${base(tenantId)}/groups/${groupId}`).then((r) => r.data)

// All or nothing, at most 100 ids. Returns { affected }.
export const bulkDeleteFaq = (tenantId, ids) =>
  api.post(`${base(tenantId)}/bulk`, { ids, action: 'delete' }).then((r) => r.data)

export const bulkSetFaqGroup = (tenantId, ids, groupId) =>
  api.post(`${base(tenantId)}/bulk`, { ids, action: 'set_group', group_id: groupId }).then((r) => r.data)

// --- Old FAQ knowledge documents (spec "Migration and compatibility") ---

// The tenant's active `doc_type=faq` documents: { id, title, size, imported_count }.
export const listFaqLegacyDocuments = (tenantId) =>
  api.get(`${base(tenantId)}/legacy`).then((r) => r.data.items)

// dryRun true only reports. Existing questions are `duplicate` and never updated;
// the document itself is never changed.
export const importFaqLegacy = (tenantId, documentId, { dryRun }) =>
  api.post(`${base(tenantId)}/legacy/import`, { document_id: documentId, dry_run: dryRun }).then((r) => r.data)

// --- Excel (spec "Excel import / export") ---

// Upload once: returns the report and an `import_token`; writes nothing.
export const previewFaqImport = (tenantId, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`${base(tenantId)}/import/preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data)
}

// Applies the previewed file by its token (the file is not sent again).
export const commitFaqImport = (tenantId, importToken, { overwriteStale = false } = {}) =>
  api.post(`${base(tenantId)}/import/commit`, { import_token: importToken, overwrite_stale: overwriteStale })
    .then((r) => r.data)

// A file download. An error body arrives as a Blob too: it is read back into
// JSON so `faqErrorCode` sees the usual `{detail: {error}}`.
async function getFile(url, params) {
  try {
    return await api.get(url, { params, responseType: 'blob' })
  } catch (error) {
    const data = error?.response?.data
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await data.text())
      } catch {
        // Not JSON: the code falls back to the HTTP status.
      }
    }
    throw error
  }
}

// filters: the list's { q, group_id, source, sync_status }; none = every entry.
export const exportFaq = (tenantId, filters = {}) => getFile(`${base(tenantId)}/export`, sentParams(filters))

export const downloadFaqTemplate = (tenantId) => getFile(`${base(tenantId)}/template`)

// The `detail` object of a FAQ error (`{error, field, existing_id, max, ...}`), or {}.
export function faqErrorDetail(error) {
  const detail = error?.response?.data?.detail
  return detail && typeof detail === 'object' ? detail : {}
}

// The FAQ routes answer `{detail: {error: code, ...}}`; older routes answer a plain string.
export function faqErrorCode(error) {
  const detail = error?.response?.data?.detail
  if (detail && typeof detail === 'object' && typeof detail.error === 'string') return detail.error
  const status = error?.response?.status
  if (status === 403) return 'permission_denied'
  if (status === 429) return 'rate_limited'
  return error?.response ? 'unknown' : 'network'
}

// A failed lookup is not "no permission": the page offers to try again.
export const RETRYABLE_FAQ_ERRORS = new Set([
  'authz_lookup_failed', 'tenant_lookup_failed', 'faq_schema_missing', 'network', 'unknown', 'rate_limited',
])

// Server values (`max`, `first_row`, ...) for a message's placeholders. Names
// that are i18next options (`count` picks a plural form, `lng` a language...) are dropped.
const I18N_OPTIONS = new Set(['count', 'context', 'lng', 'lngs', 'ns', 'defaultValue', 'returnObjects', 'replace'])
export const faqMessageParams = (values = {}) => Object.fromEntries(
  Object.entries(values).filter(([name]) => !I18N_OPTIONS.has(name)),
)

export function faqErrorMessage(t, error) {
  const code = faqErrorCode(error)
  return t(`faq.errors.${code}`, { ...faqMessageParams(faqErrorDetail(error)), defaultValue: t('faq.errors.unknown') })
}
